import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { ConsignmentNumberFilter, OrganizationSelectFilter } from '@/features/dashboard-filters';
import { FilterSelect, type SelectOption } from '@/shared/ui';
import { TextFilter } from '@/shared/ui/filters';

import type { ReceiveUrlState } from '../model/types';

interface ReceiveFiltersProps {
  filters: ReceiveUrlState;
  onFilterChange: (key: keyof ReceiveUrlState, value: string) => void;
  organizationOptions: SelectOption[];
  selectedOrganization?: string;
  onOrganizationChange: (value?: string) => void;
  onOrganizationSearchChange: (value: string) => void;
  isLoadingOrganizations: boolean;
}

/**
 * Receive filter popover content. Reuses the shared field components used by the
 * consignments filter bar (`ConsignmentNumberFilter`, `OrganizationSelectFilter`,
 * `FilterSelect`) rather than hand-rolled label+input blocks.
 */
export function ReceiveFilters({
  filters,
  onFilterChange,
  organizationOptions,
  selectedOrganization,
  onOrganizationChange,
  onOrganizationSearchChange,
  isLoadingOrganizations
}: ReceiveFiltersProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-start gap-4">
      <ConsignmentNumberFilter
        value={filters.consignmentNumber}
        onChange={(value) => onFilterChange('consignmentNumber', value)}
      />

      <OrganizationSelectFilter
        options={organizationOptions}
        value={selectedOrganization}
        onChange={onOrganizationChange}
        onSearchChange={onOrganizationSearchChange}
        isLoading={isLoadingOrganizations}
      />

      <div className="flex flex-col gap-1">
        <FilterSelect
          label={t('receive.dashboard.filters.originLocation', 'Origin Location')}
          value={filters.originLocation}
          onChange={(value) => onFilterChange('originLocation', value)}
          options={[{ value: '', label: t('common.all', 'All') }]}
          width="full"
          className="w-full md:w-60"
        />
        <span className="text-xs text-muted-foreground">
          {t('receive.dashboard.filters.originLocationHelper', 'Only for incoming transfers')}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t('receive.dashboard.filters.shipmentId', 'Shipment ID')}</Label>
        <TextFilter
          value={filters.shipmentIdentification}
          onChange={(value) => onFilterChange('shipmentIdentification', value)}
          placeholder={t('receive.dashboard.filters.shipmentIdPlaceholder', 'Shipment identification')}
          className="w-full md:w-60"
        />
      </div>
    </div>
  );
}
